/**
 * FAQ & Help Page - Trade Loop v2.0
 *
 * Comprehensive help guide with common questions, gotchas, and tips
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { usePricing } from "@/hooks/usePricing";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  HelpCircle,
  Users,
  Send,
  Settings,
  CreditCard,
  Shield,
  AlertTriangle
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface FAQItem {
  question: string;
  answer: string;
  isGotcha?: boolean;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  questions: FAQItem[];
}

export default function FAQPage() {
  const { isAuthenticated } = useAuth();
  const { getPlanPriceFormatted } = usePricing();
  const [openCategory, setOpenCategory] = useState<number | null>(0);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  // If user is not authenticated, render without AppLayout (no headers/footers)
  const isPublicView = !isAuthenticated;

  const categories: FAQCategory[] = [
    {
      title: "Getting Started",
      icon: <HelpCircle className="w-5 h-5 text-blue-600" />,
      iconBg: "bg-blue-100",
      questions: [
        {
          question: "What is Trade Loop?",
          answer: "Trade Loop is a professional referral networking platform that helps businesses connect and grow through trusted partner relationships. Send and receive referrals, track your progress, and build a valuable network of trade connections."
        },
        {
          question: "How do I create an account?",
          answer: "Click 'Sign Up' on the login page. Enter your business email, create a password, and complete your profile with your company information, trade, and service areas. You'll start on the free Starter plan, which includes unlimited referrals and unlimited partner connections."
        },
        {
          question: "Do I need to verify my email?",
          answer: "Yes! After signing up, check your email for a verification link. Click it to activate your account. If you don't see the email, check your spam folder or request a new verification email from the login page.",
          isGotcha: true
        },
        {
          question: "How do I update my profile?",
          answer: "Go to Settings → Your Profile → Edit Profile. You can update your name, company, trade, service areas, and profile picture. Changes are saved immediately."
        }
      ]
    },
    {
      title: "Sending Referrals",
      icon: <Send className="w-5 h-5 text-green-600" />,
      iconBg: "bg-green-100",
      questions: [
        {
          question: "How do I send a referral?",
          answer: "From the Dashboard or Referrals page, click 'Send Referral'. Select a partner from your network, enter the customer's information (name, contact details, job description, and location), add any notes, and submit. The partner will receive an in-app notification."
        },
        {
          question: "What information is required for a referral?",
          answer: "Required: Recipient (partner), Customer Name, Job Description, and Job Location. Optional: Customer Email, Customer Phone, Estimated Value, Referral Fee, Notes, and Attachments."
        },
        {
          question: "Are customer details encrypted?",
          answer: "Yes! Customer names, emails, phone numbers, and job locations are automatically encrypted before being stored in the database. This protects sensitive client information while still allowing you to manage your referrals."
        },
        {
          question: "Can I send a referral without customer contact info?",
          answer: "Yes, customer email and phone are optional fields. However, providing contact information helps your partner reach the customer more easily."
        },
        {
          question: "Why didn't my referral send?",
          answer: "Common issues: (1) The recipient is not in your network - add them first from the Network page. (2) Missing required fields - check that Customer Name, Job Description, and Job Location are filled.",
          isGotcha: true
        },
        {
          question: "How many referrals can I send?",
          answer: "As many as you like. Every plan - Starter, Pro, and Grow - includes unlimited referrals and unlimited partner connections, so there's no monthly cap to keep track of. Your recent referral activity is shown on the Dashboard."
        }
      ]
    },
    {
      title: "Managing Connections",
      icon: <Users className="w-5 h-5 text-purple-600" />,
      iconBg: "bg-purple-100",
      questions: [
        {
          question: "How do I add partners to my network?",
          answer: "Go to Network → Add Partner. Search for users by name, company, or trade. Click 'Connect' to send a connection request. Once they accept, you can send referrals to each other."
        },
        {
          question: "What's the difference between connections and partners?",
          answer: "They're the same thing! 'Connections' and 'partners' both refer to the professionals in your network that you can send referrals to and receive referrals from."
        },
        {
          question: "Can I connect with anyone on Trade Loop?",
          answer: "Yes! Search for any user and send them a connection request. They'll receive a notification and can accept or decline. Focus on building connections with complementary trades to maximize referral opportunities."
        },
        {
          question: "How do I disconnect from someone?",
          answer: "Go to Network, find the connection, and click the three-dot menu → 'Remove Connection'. This will permanently remove them from your network. You won't be able to send or receive referrals from them anymore."
        },
        {
          question: "Why can't I send a referral to someone?",
          answer: "You can only send referrals to users in your network. If you don't see them in the recipient dropdown, go to the Network page and add them as a connection first. Wait for them to accept your request.",
          isGotcha: true
        }
      ]
    },
    {
      title: "Account Settings",
      icon: <Settings className="w-5 h-5 text-gray-600" />,
      iconBg: "bg-gray-100",
      questions: [
        {
          question: "How do I change my password?",
          answer: "Currently, password changes are handled through the forgot password flow. Click 'Forgot Password' on the login page, enter your email, and follow the reset link. A dedicated password change feature is coming soon in Settings."
        },
        {
          question: "Can I change my email address?",
          answer: "Email changes must be done by contacting support. This security measure prevents unauthorized account takeover. Contact us through Settings → Contact Support with your new email address."
        },
        {
          question: "How do I upload a profile picture?",
          answer: "Go to Settings → Your Profile. Click on your current avatar (or the placeholder), select 'Upload Image', choose a file, and click 'Upload'. Supported formats: JPG, PNG, GIF. Max size: 5MB."
        },
        {
          question: "Why won't my profile picture upload?",
          answer: "Common issues: (1) File too large - must be under 5MB. (2) Unsupported format - use JPG, PNG, or GIF only. (3) Poor internet connection - try again with a stable connection. If problems persist, try a different image.",
          isGotcha: true
        },
        {
          question: "How do I delete my account?",
          answer: "Go to Settings → Delete Account. You'll be asked to confirm the deletion. WARNING: This action is permanent and cannot be undone. All your referrals, connections, and data will be permanently deleted."
        }
      ]
    },
    {
      title: "Plans & Billing",
      icon: <CreditCard className="w-5 h-5 text-indigo-600" />,
      iconBg: "bg-indigo-100",
      questions: [
        {
          question: "What plans are available?",
          answer: `Three plans, and all of them include unlimited referrals and unlimited partner connections. Starter (Free) - one trade selection, basic profile sharing, standard messaging, community support. Pro (${getPlanPriceFormatted('pro')}) - unlimited trades, company logo and branding, partner favorites and filtering, priority support. Grow (${getPlanPriceFormatted('grow')}) - everything in Pro plus team collaboration, custom integrations, and a dedicated account manager. Grow is coming soon and isn't available to purchase yet.`
        },
        {
          question: "Can I upgrade or downgrade my plan?",
          answer: "Yes! Go to Settings → View / Change Plan. Select your desired plan and confirm. Upgrades take effect immediately. Downgrades take effect at the start of your next billing cycle. You'll keep your current plan benefits until then. Grow is shown as 'Coming Soon' and can't be selected yet."
        },
        {
          question: "Are there limits on how many referrals I can send?",
          answer: "No! All plans (Starter, Pro, and Grow) include unlimited referrals and unlimited connections. The main differences between plans are in advanced features like professional profiles, team collaboration, and custom integrations.",
          isGotcha: false
        },
        {
          question: "Do unused referrals roll over to the next month?",
          answer: "There's nothing to roll over. Referrals are unlimited on every plan, so there's no monthly allowance to use up or carry forward."
        },
        {
          question: "Can I cancel my subscription anytime?",
          answer: "Yes, cancel anytime from Settings → View / Change Plan. You'll keep access to your paid plan features until the end of your billing period, then you'll be automatically moved to the free Starter plan."
        }
      ]
    },
    {
      title: "Privacy & Security",
      icon: <Shield className="w-5 h-5 text-red-600" />,
      iconBg: "bg-red-100",
      questions: [
        {
          question: "Is my data secure?",
          answer: "Yes! All data is encrypted in transit (HTTPS) and at rest. Customer information (names, emails, phones, locations) is encrypted with AES-256 encryption. We use Supabase for secure authentication and PostgreSQL for database security."
        },
        {
          question: "Who can see my referrals?",
          answer: "Only you and the recipient can see each referral. Customer details are encrypted and only visible to the sender and receiver of that specific referral. Other users cannot see your referral history or customer information."
        },
        {
          question: "Can I hide or delete a referral?",
          answer: "Currently, you cannot delete referrals as they're part of your business record. However, you can hide referrals from your view (coming soon). If you sent a referral by mistake, contact the recipient to let them know."
        },
        {
          question: "Does Trade Loop sell my data?",
          answer: "No, never. We do not sell, rent, or share your personal information or customer data with third parties for marketing purposes. Your data is used only to provide and improve the Trade Loop service."
        },
        {
          question: "What happens to my data if I delete my account?",
          answer: "All your personal data, profile information, and referral records are permanently deleted within 30 days. Encrypted customer data in referrals you sent is also deleted. However, referrals you received remain in other users' accounts for their records."
        }
      ]
    },
    {
      title: "Common Issues & Gotchas",
      icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
      iconBg: "bg-amber-100",
      questions: [
        {
          question: "Why can't I log in?",
          answer: "Common reasons: (1) Email not verified - check your inbox for verification email. (2) Wrong password - try 'Forgot Password'. (3) Account doesn't exist - try signing up instead. (4) Caps Lock is on - passwords are case-sensitive.",
          isGotcha: true
        },
        {
          question: "The app says 'Session expired' - what does this mean?",
          answer: "Your login session has timed out for security. Click 'Refresh' or log in again. Sessions expire after 7 days of inactivity or immediately if you change your password.",
          isGotcha: true
        },
        {
          question: "Why don't I see any referrals on my Dashboard?",
          answer: "You might not have sent or received any referrals yet! The Dashboard shows recent activity. Send your first referral by clicking 'Send Referral' from the Dashboard or Referrals page. Once you have connections, you'll start receiving referrals from them.",
          isGotcha: true
        },
        {
          question: "The recipient dropdown is empty when sending a referral",
          answer: "This means you don't have any connections yet! Go to Network → Add Partner to search and connect with other Trade Loop users first. Once they accept your connection request, they'll appear in the recipient dropdown.",
          isGotcha: true
        },
        {
          question: "Why did my referral show as 'pending' for so long?",
          answer: "Referrals stay 'pending' until the recipient takes action (accepts, declines, or marks as completed). The recipient controls the status. Follow up directly with them if a referral has been pending for a while.",
          isGotcha: true
        },
        {
          question: "I clicked 'Send Referral' but nothing happened",
          answer: "Check for error messages or validation warnings. Common issues: Missing required fields (customer name, job description, job location) or no recipient selected. Fix the errors and try again.",
          isGotcha: true
        },
        {
          question: "My profile image won't update",
          answer: "Try these solutions: (1) Ensure the image is under 5MB and in JPG/PNG/GIF format. (2) Clear your browser cache and refresh. (3) Try a different image. (4) Check your internet connection. If problems persist, contact support.",
          isGotcha: true
        },
        {
          question: "Can I use Trade Loop on mobile?",
          // Do not restore the old "add it to your home screen" advice. This page is
          // reachable from inside the native app (Profile Settings links to /faq), so that
          // line told people using the installed app to install a home-screen web version
          // of it instead. The PWA is being retired — Card 30.
          answer: "Yes! Trade Loop has native apps for iPhone, iPad, and Android, and it also works on mobile browsers (Safari, Chrome, Firefox)."
        },
        {
          question: "What browsers are supported?",
          answer: "Trade Loop works best on modern browsers: Chrome 90+, Firefox 88+, Safari 14+, Edge 90+. If you experience issues, try updating your browser or switching to a supported browser."
        }
      ]
    }
  ];

  const toggleCategory = (index: number) => {
    setOpenCategory(openCategory === index ? null : index);
    setOpenQuestion(null);
  };

  const toggleQuestion = (categoryIndex: number, questionIndex: number) => {
    const questionId = `${categoryIndex}-${questionIndex}`;
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  // Public view content (no headers/footers for non-authenticated users)
  const pageContent = (
    <div className="px-4 py-6 pb-[44px] md:pb-6">
      <div className="max-w-4xl mx-auto">
        {/* Back to Landing button for public users */}
        {isPublicView && (
          <div className="mb-6">
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2">
                <ChevronDown className="w-4 h-4 rotate-90" />
                Back to Home
              </Button>
            </Link>
          </div>
        )}

        {/* Page Title */}
        <div className="text-center mb-10 animate-in slide-in-from-bottom-4 duration-500">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
            FAQ & Help
          </h2>
          <p className="text-gray-600 text-lg">
            Find answers to common questions and get help using Trade Loop
          </p>
        </div>

          {/* FAQ Categories */}
          <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500 delay-150">
            {categories.map((category, categoryIndex) => (
              <div
                key={categoryIndex}
                className="bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-xl overflow-hidden transition-all duration-200 hover:shadow-md"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryIndex)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50/50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 ${category.iconBg} rounded-xl`}>
                      {category.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {category.title}
                    </h3>
                    <span className="text-sm text-gray-500 font-medium">
                      ({category.questions.length} questions)
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      openCategory === categoryIndex ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Questions */}
                {openCategory === categoryIndex && (
                  <div className="border-t border-gray-200/50">
                    {category.questions.map((item, questionIndex) => (
                      <div
                        key={questionIndex}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        {/* Question */}
                        <button
                          onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          className="w-full flex items-start justify-between p-5 text-left hover:bg-gray-50/30 transition-colors duration-200"
                        >
                          <div className="flex items-start gap-3 flex-1">
                            {item.isGotcha && (
                              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            )}
                            <p className="font-medium text-gray-800">
                              {item.question}
                            </p>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 mt-1 ${
                              openQuestion === `${categoryIndex}-${questionIndex}`
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>

                        {/* Answer */}
                        {openQuestion === `${categoryIndex}-${questionIndex}` && (
                          <div className="px-5 pb-5 pt-0">
                            <div
                              className={`pl-7 text-gray-600 leading-relaxed ${
                                item.isGotcha ? "ml-7" : ""
                              }`}
                            >
                              {item.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Still Need Help */}
          <div className="mt-10 text-center animate-in slide-in-from-bottom-4 duration-500 delay-300">
            <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-200/50 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Still Need Help?
              </h3>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/contact-us">
                  <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white">
                    Contact Support
                  </Button>
                </Link>
                {isAuthenticated && (
                  <Link to="/settings">
                    <Button variant="outline" className="border-blue-300 hover:bg-blue-50">
                      Back to Settings
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  // For authenticated users, wrap in AppLayout (includes headers/footers)
  // For public users, render content directly (no headers/footers)
  return isPublicView ? (
    <div className="min-h-screen bg-gray-50 pt-5 md:pt-0">
      {pageContent}
    </div>
  ) : (
    <AppLayout title="FAQ & Help" backLink="/settings">
      {pageContent}
    </AppLayout>
  );
}
